import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ItemList from '../../../Common/ItemList/ItemList';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import DialogActions from '../../../Common/Dialog/DialogActions';
import DialogContent from '../../../Common/Dialog/DialogContent';
import DialogButton from '../../../Common/Buttons/DialogButton';
import { FetchUserStorePresets } from '../../../UserData/_actions/Store/UserStorePresetActions';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import DialogTitle from '../../../Common/Dialog/DialogTitle';
import ProductSearchBar, { type ProductSearchParams } from '../../Products/ProductSearchBar';
import type { UserProductDesignStore } from '../../../UserData/_stores/UserProductDesignStore';
import ProductListingCard from './ProductListingCard';
import ProductSkeletonList from '../../Products/ProductSkeletonList';
import useShiftRangeSelection from '../../../Common/_hooks/useShiftRangeSelection';
import AccountNoResults from '../../AccountNoResults';
import AccountFilterDrawer from '../../Filters/AccountFilterDrawer';
import AccountFilterSection from '../../Filters/AccountFilterSection';
import AccountLayoutFilter from '../../Filters/AccountLayoutFilter';
import AccountSortFilter, { type AccountSortOption } from '../../Filters/AccountSortFilter';
import type { ProductSortType } from '../../../UserData/_stores/UserPrefsStore';
import { SaveUserPrefs } from '../../../UserData/_actions/UserDataActions';

export interface Props {
	index: number
	store: string
	handleStep: (step: number) => void
	onDialogClose: () => void
}

const SEARCH_FIELDS = ['search', 'categories', 'collections', 'sort', 'showAlreadyPublished', 'publishedStore']

export default function SelectProduct(props: Props) {
	const dispatch = useAppDispatch();
	const [t] = useTranslation('account-v2');

	const userLayout = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsLayout'))
	const userLimit = useAppSelector(state => state.get('userData').get('prefs').get('accountPageLimit'))
	const userSort = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsSort'))
	const listings = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings'))
	const listId = useMemo(() => 'add-product-listing-product-designs', [])
	const visibleProductDesignItemIds = useAppSelector(state =>
		state.get('userData')
			.get('productDesigns')
			.get('itemLists')
			.get(listId)
			?.get('items')
	)
	const visibleProductDesignIds = useMemo(() => (
		visibleProductDesignItemIds
			?.valueSeq()
			.map(id => Number(id))
			.filter((id): id is number => Number.isFinite(id))
			.toArray() ?? []
	), [visibleProductDesignItemIds])
	const { getSelectionIds } = useShiftRangeSelection(visibleProductDesignIds)

	const [layout, setLayout] = useState(userLayout)
	const [search, setSearch] = useState<ProductSearchParams>({
		categories: [],
		search: [],
		collections: [],
		sort: [userSort],
		showAlreadyPublished: ['1'],
		publishedStore: [props.store],
	})
	const defaultSearch = useMemo<ProductSearchParams>(() => ({
		categories: [],
		search: [],
		collections: [],
		sort: [userSort],
		showAlreadyPublished: ['1'],
		publishedStore: [props.store],
	}), [props.store, userSort])

	useEffect(() => {
		dispatch(FetchUserStorePresets())
	}, [dispatch])

	useEffect(() => {
		setSearch(value => {
			if (value.publishedStore[0] === props.store) {
				return value
			}

			return {
				...value,
				publishedStore: [props.store],
			}
		})
	}, [props.store])

	const onClickSelectProducts = useCallback(() => {
		props.handleStep(props.index + 1)
	}, [props.handleStep, props.index])

	const onChangeSort = useCallback((nextSort: ProductSortType) => {
		setSearch(value => ({
			...value,
			sort: [nextSort],
		}))

		if (nextSort === userSort) {
			return
		}

		const fd = new FormData()
		fd.append('accountProductsSort', nextSort)
		dispatch(SaveUserPrefs(fd, true))
	}, [dispatch, userSort])

	useEffect(() => {
		setSearch(value => {
			if (value.sort[0] === userSort) {
				return value
			}

			return {
				...value,
				sort: [userSort],
			}
		})
	}, [userSort])

	const onChangeAlreadyPublished = useCallback((_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		setSearch(value => ({
			...value,
			showAlreadyPublished: [checked ? '1' : '0'],
		}))
	}, [])

	const renderItem = useCallback((item: UserProductDesignStore) => {
		return <ProductListingCard
			item={item}
			view={layout}
			store={props.store}
			getSelectionIds={getSelectionIds}
		/>
	}, [layout, props.store, getSelectionIds])

	const renderList = useCallback((items: React.JSX.Element | null, links: React.JSX.Element | null, loading: boolean) => {
		return <>
			<Grid container spacing={{ xs: 2, md: 3 }}>
				{loading
					? <ProductSkeletonList count={userLimit} layout={layout} showActions={false}/>
					: (items ?? <Grid item xs={12}>
						<AccountNoResults compact />
					</Grid>
					)
				}
			</Grid>

			{links}
		</>
	}, [userLimit, layout])

	const selectedSort = search.sort[0] ?? userSort
	const showAlreadyPublished = search.showAlreadyPublished[0] !== '0'
	const sortOptions: AccountSortOption[] = [
		{ value: 'n-to-o', label: t('Newest first') },
		{ value: 'o-to-n', label: t('Oldest first') },
		{ value: 'a-to-z', label: t('A to Z') },
		{ value: 'z-to-a', label: t('Z to A') },
	]

	return <>
		<DialogTitle>{t('{{count}} Selected products', { count: listings.count() })}</DialogTitle>
		<DialogContent>
			<Typography variant="body1" sx={{ mb: 2 }}>{t('Select your Art of Where products that you want to add to your store.')}</Typography>
			<ProductSearchBar
				placeholder={t('Search your products...')}
				value={search}
				onChange={setSearch}
			>
				<AccountFilterDrawer inDialog activeCount={showAlreadyPublished ? 0 : 1}>
					<AccountFilterSection title={t('Filters')}>
						<FormControlLabel
							control={<Checkbox
								size="small"
								checked={showAlreadyPublished}
								onChange={onChangeAlreadyPublished}
							/>}
							label={t('Show already published products')}
						/>
					</AccountFilterSection>
					<AccountFilterSection title={t('Display')}>
						<AccountSortFilter
							label={t('Sort by')}
							value={selectedSort}
							options={sortOptions}
							onChange={onChangeSort}
							SelectBaseProps={{
								MenuProps: {
									sx: {
										zIndex: theme => theme.zIndex.modal + 2,
									},
									PaperProps: {
										sx: {
											zIndex: theme => theme.zIndex.modal + 2,
										},
									},
								},
							}}
						/>
						<AccountLayoutFilter
							label={t('Layout')}
							value={layout}
							onChange={setLayout}
						/>
					</AccountFilterSection>
				</AccountFilterDrawer>
			</ProductSearchBar>
			<ItemList
				listId={listId}
				controlled
				dataType={'productDesigns'}
				item={renderItem}
				searchQuery={search}
				defaultSearchQuery={defaultSearch}
				searchFields={SEARCH_FIELDS}
				limit={userLimit}
				render={renderList}
			/>
		</DialogContent>

		<DialogActions>
			<DialogButton
				color="secondary"
				onClick={props.onDialogClose}
			>{t('Cancel')}</DialogButton>
			<DialogButton
				color="accent"
				onClick={onClickSelectProducts}
				disabled={listings.count() === 0}
			>{t('Select {{count}} Products', { count: listings.count() })}</DialogButton>
		</DialogActions>
	</>;
}
